import { fromEvent, map, filter, distinctUntilChanged, tap, skip, take, pairwise, startWith } from 'rxjs';
import { store, store$ } from '@redux/store';
import {
  searchParametersActions,
  selectResultType,
  searchParametersSelectors,
  selectProductAndResultType,
  ResultType
} from 'features/searchResultsFacets';
import uiStateSlice from 'layout/redux/uiStateSlice';
import fastdom from 'fastdom';
import HomeListings from 'constants/hashUrl';
import PageLayout from '../../../layout/constants/pageLayoutNames';
import getResultTabConfiguration from '../services/resultTabConfiguration';
import getEventTabConfiguration from '../analytics/eventTabConfiguration';

const { selectProductType } = searchParametersSelectors;
const resultTabContainerElement = document.querySelector('[data-result-type-bar]');

// need to listen to changes in the result type and change the currently active result type
const onResultsTypeChanged$ = store$.pipe(map(selectResultType), filter(Boolean), distinctUntilChanged());

const onTabChanged$ = store$.pipe(map(selectProductAndResultType), filter(Boolean), distinctUntilChanged());

const tabs = document.querySelectorAll('[data-result-type-bar]');
const tabsClicks$ = fromEvent(tabs, 'click');

// observable created when there is a change in the tabs.
const resultType$ = tabsClicks$.pipe(
  filter((event) => event.target.hasAttribute('data-result-type')),
  tap((event) => event.preventDefault()),
  filter((event) => !event.target.dataset.active),
  map((event) => event.target.dataset),
  filter(Boolean)
);

const uiStateActions = uiStateSlice.actions;

/**
 * Determines if the current URL hash matches the "Homes" value.
 * @returns {boolean} True if the URL hash is equal to "Homes", otherwise false.
 */
const shouldClickHomeTab = function() {
  const urlHash = window.location.hash === HomeListings;
  return urlHash;
};

// triggers the action to update the page layout.
const triggerUpdateLayout = (payload) => {
  const { productType, resultType } = payload;
  const key = `${productType}-${resultType}`;
  const pageLayoutName = PageLayout.fromKey(key).name;
  store.dispatch(uiStateActions.updatePageLayout(pageLayoutName));
};

onTabChanged$.subscribe(triggerUpdateLayout);

// changes the tabs bar active element
onResultsTypeChanged$.subscribe((value) => {
  const types = resultTabContainerElement.querySelectorAll('[data-result-type]');
  const element = resultTabContainerElement.querySelector(`[data-result-type="${value}"]`);

  if (element) {
    types.forEach((sibling) => {
      sibling.classList.remove('nhs-n1-c-button__tab--light-active');
      sibling.removeAttribute('data-active');
    });

    element.classList.add('nhs-n1-c-button__tab--light-active');
    element.dataset.active = true;
  }
});

/**
 * updateResultType: Updates the Result type information on the store.
 * @param {Object} data Object with the information that relates to the selected result type.
 */
function updateResultType(data) {
  const { resultType, productType } = data;
  store.dispatch(
    searchParametersActions.fetchResultsType({
      productType,
      resultType,
    })
  );
}

/**
 * The function dispatches a user event by retrieving the event configuration based on the product type
 * and result type, and then dispatches the event to track the user event using Google Analytics.
 * @param data - The `data` parameter is an object that contains information about the user event. It
 * has two properties:
 */
function dispatchUserEvent(data) {
  setTimeout(() => {
    const { productType, resultType } = data;
    const googleAnalyticsDTO = getEventTabConfiguration(productType, resultType);
    if (googleAnalyticsDTO) {
      store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));
    }
  });
}

// dispatch the action of the result type change
resultType$.subscribe(dispatchUserEvent);
resultType$.subscribe(updateResultType);

onResultsTypeChanged$.pipe(
  startWith(null),
  pairwise(),
  filter(([previous, current]) => previous === null && current === ResultType.Communities.toString()),
  filter(shouldClickHomeTab),
  take(1)
).subscribe(() => {
  const homeTab = resultTabContainerElement.querySelector(`[data-result-type="homes"]`);
  if(homeTab){
    homeTab.click();
  }
});

const resultTabsChanged$ = store$.pipe(
  map(selectProductType),
  distinctUntilChanged(),
  map((productType) => getResultTabConfiguration(productType)),
  distinctUntilChanged(),
  skip(1)
);

/**
 * Removes the current tabs from the DOM.
 */
function removeAllTabs() {
  while (resultTabContainerElement.firstChild) {
    resultTabContainerElement.removeChild(resultTabContainerElement.firstChild);
  }
}

/**
 * Updates the list of tabs that will be shown on the DOM.
 * @param {Array} newTabs List of tab configurations to set.
 */
function updateTabs(newTabs) {
  fastdom.mutate(() => {
    removeAllTabs();
    newTabs.forEach((tabConfiguration) => {
      const tab = document.createRange().createContextualFragment(tabConfiguration.getHTML());
      resultTabContainerElement.appendChild(tab);
    });
  })
}

resultTabsChanged$.subscribe(updateTabs);
